import {useApolloClient, useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {
    GetTagsBasic_tags_edges_node as Tag,
    GetTagsBasic as GetTagsBasicQuery,
    TagType,
} from 'nutshell-graphql-types';

import GET_TAGS from '../queries/tags.graphql';

export const GET_TAGS_LIMIT = 1000;

export const useGetTags = ({
    searchTerm,
    tagType,
    archived,
}: {
    searchTerm?: string;
    tagType: TagType;
    archived?: boolean;
}): {
    isLoading: boolean;
    errorMessage: string | null | undefined;
    tags: Tag[] | null | undefined;
    totalCount: number;
    hasNextPage: boolean;
    refetch: () => Promise<any>;
    fetchMore: (searchTerm: string, tagType: TagType) => Promise<any>;
} => {
    const variables = {
        first: GET_TAGS_LIMIT,
        input: {
            searchTerm,
            entityType: tagType,
            archived: archived === true ? true : undefined,
        },
    } as const;

    const client = useApolloClient();
    const {loading, data, refetch, fetchMore, error} = useQuery<
        GetTagsBasicQuery,
        Record<any, any>
    >(GET_TAGS, {
        notifyOnNetworkStatusChange: true,
        variables,
        fetchPolicy: 'cache-and-network',
    });

    const hasNextPage = data && data.tags && data.tags.pageInfo && data.tags.pageInfo.hasNextPage;
    const endCursor = data && data.tags && data.tags.pageInfo && data.tags.pageInfo.endCursor;
    const totalCount = data && data.tags ? data.tags.totalCount : 0;
    const hasCacheForVars = hasCachedDataForVariables(client, variables);
    const shouldShowTags = !loading || hasCacheForVars;

    return {
        isLoading: loading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
        tags:
            shouldShowTags && data && data.tags && data.tags.edges
                ? data.tags.edges.map((edge) => edge.node)
                : null,
        totalCount,
        hasNextPage: hasNextPage || false,
        refetch: refetch,
        // this hook is being used in multiple places but the endCursor is only used in one place, but it breaks the fetch in other places so we need to pass it as an optional parameter
        fetchMore: (search: string, type: TagType, shouldPaginate?: boolean) => {
            return fetchMore({
                query: GET_TAGS,
                variables: {
                    first: GET_TAGS_LIMIT,
                    after: shouldPaginate && endCursor,
                    input: {
                        entityType: type,
                        searchTerm: search,
                        archived: archived === true ? true : undefined,
                    },
                },
                // @ts-expect-error
                updateQuery: (previousResult, {fetchMoreResult}) => {
                    if (!fetchMoreResult || !previousResult) return;

                    return {
                        tags: {
                            __typename: 'TagsConnection',
                            // @ts-expect-error
                            pageInfo: fetchMoreResult.tags.pageInfo,
                            // @ts-expect-error
                            edges: [...previousResult.tags.edges, ...fetchMoreResult.tags.edges],
                        },
                    };
                },
            });
        },
    };
};

// Apollo will return data from the previous result even if there is no data for the new variables.
// This leads to confusing UI. A user could get lead tags, then switch to company tags and see the lead tags still.
// This function checks if the cache has data for the given variables.
// @ts-expect-error
function hasCachedDataForVariables(client, variables: any): boolean {
    try {
        const cached = client.readQuery({query: GET_TAGS, variables});

        return Boolean(cached && cached.tags && cached.tags.edges);
    } catch (e: any) {
        return false;
    }
}
